import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { loadItem } from '@pn/core/loaders/workspace/loadWorkspaceItem';
import { removeLayers } from '@pn/core/operations/workspace/layersProcessing';
import {
  useCurrentUserStorage,
  useWorkspaceStorage,
  workspaceActions,
} from '@pn/core/storage';
import { isCordova } from '@pn/core/utils/env';
import { notificationService } from '@pn/services/notifications';
import { useScreenSize } from '@pn/ui/hooks/useScreenSize';
import { isNil } from 'lodash-es';
import React from 'react';

type Tab = 'Content' | 'Style';

type ContextType = {
  isWorkspaceItemPanelOpen: boolean;
  isDrawingPanelOpen: boolean;
  liveItemOpened: WorkspaceItem | undefined;
  tab: Tab;
  promptToRename: boolean;
  setTab: (tab: Tab) => void;
  openWorkspaceItemPanel: (params: {
    item: WorkspaceItem;
    tab?: Tab;
    promptToRename?: boolean;
  }) => void;
  closeWorkspaceItemPanel: (skipReset?: boolean) => void;
  resetCurrentItem: () => void;
};

const WorkspaceItemPanelContext = React.createContext({} as ContextType);
export const useWorkspaceItemPanel = () =>
  React.useContext(WorkspaceItemPanelContext);

type ContextProviderProps = {
  children: React.ReactNode;
};

export const WorkspaceItemPanelProvider = ({
  children,
}: ContextProviderProps) => {
  const { smScreen } = useScreenSize();

  const { allWorkspaceItems } = useWorkspaceStorage();
  const { stackCompanyId } = useCurrentUserStorage();

  const [isOpen, setIsOpen] = React.useState(false);
  const [itemOpened, setItemOpened] = React.useState<
    WorkspaceItem | undefined
  >();
  const [tab, setTab] = React.useState<Tab>('Content');
  const [promptToRename, setPromptToRename] = React.useState(false);

  const liveItemOpened = React.useMemo(() => {
    return !isNil(itemOpened?.id)
      ? allWorkspaceItems[itemOpened?.id]
      : undefined;
  }, [allWorkspaceItems, itemOpened?.id]);

  const resetCurrentItem = React.useCallback(() => {
    if (isNil(itemOpened) || isNil(liveItemOpened)) return;
    resetItem(itemOpened, liveItemOpened);
  }, [itemOpened, liveItemOpened]);

  const openWorkspaceItemPanel = React.useCallback(
    async (params: {
      item: WorkspaceItem;
      tab?: Tab;
      promptToRename?: boolean;
    }) => {
      const { item, tab, promptToRename } = params;

      if (item.itemType === 'drawing' && (smScreen || isCordova())) {
        notificationService.warning(
          'Drawing is not available on mobile devices'
        );
        return;
      }

      if (item.id !== itemOpened?.id) resetCurrentItem();

      setItemOpened(item);
      setIsOpen(true);
      if (tab) setTab(tab);
      setPromptToRename(promptToRename ?? false);

      if (!item.isLoaded) {
        const loadedItem = await loadItem(item, stackCompanyId);
        setItemOpened(loadedItem);
      }
    },
    [smScreen, itemOpened, resetCurrentItem, stackCompanyId]
  );

  const closeWorkspaceItemPanel = React.useCallback(() => {
    setIsOpen(false);
    setItemOpened(undefined);
  }, []);

  const value = React.useMemo(
    () => ({
      isWorkspaceItemPanelOpen: isOpen,
      isDrawingPanelOpen: isOpen && liveItemOpened?.itemType === 'drawing',
      liveItemOpened,
      tab,
      promptToRename,
      setTab,
      openWorkspaceItemPanel,
      closeWorkspaceItemPanel,
      resetCurrentItem,
    }),
    [
      isOpen,
      liveItemOpened,
      tab,
      promptToRename,
      openWorkspaceItemPanel,
      closeWorkspaceItemPanel,
      resetCurrentItem,
    ]
  );

  return (
    <WorkspaceItemPanelContext.Provider value={value}>
      {children}
    </WorkspaceItemPanelContext.Provider>
  );
};

/**
 * @param itemOpened - the state of the item when it was first opened
 * @param liveItem - the current state of the item
 */
function resetItem(itemOpened: WorkspaceItem, liveItem: WorkspaceItem): void {
  if (!isNil(liveItem.module) || liveItem.isTemporary) {
    return;
  }

  /**
   * Remove layers that were added to the item while the panel was open.
   */
  const diffLayers = liveItem.map.layers.filter(
    (layer) => !itemOpened.map.layers.find(({ id }) => layer.id === id)
  );
  removeLayers(diffLayers);

  workspaceActions().overwrite({
    ...itemOpened,
    name: liveItem.name, // name is auto-saved
    isShared: liveItem.isShared, // shared status is saved immediately upon change
    query: {
      ...liveItem.query,
      scopeIds: itemOpened.query.scopeIds,
    },
  });
  workspaceActions().revisualize(itemOpened.id);
}
